import { groupBy } from 'lodash-es';
import { createCachedSelector } from 're-reselect';

import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { createEntitiesIndexSelector } from '@iconik-io/shared-lib/src/redux/entities_index/selectors';

import type { AppState } from '../../store/types';

const getTranscriptionProperties = (state: AppState) =>
  state.entities.transcription_properties;

const getAllSelectedTranscriptionPropertiesIds = (state: AppState) =>
  state.assetTranscriptions.selectedTranscriptionPropertiesIdByAssetId;

const getAssetId = (_: AppState, assetId: string) => assetId;
const getVersionId = (
  _: AppState,
  _assetId: string,
  versionId: string | undefined,
) => versionId;

const selectAssetTranscriptionProperties = createEntitiesIndexSelector(
  'transcription_properties',
  'asset_id',
);

const selectAssetSegments = createEntitiesIndexSelector('segments', 'asset_id');

export const getSelectedTranscriptionPropertiesIdByAssetId =
  createCachedSelector(
    getAllSelectedTranscriptionPropertiesIds,
    getAssetId,
    (selectedTranscriptionPropertiesIds, assetId) =>
      selectedTranscriptionPropertiesIds[assetId],
  )((_state: AppState, assetId: string) => assetId);

export const selectTranscriptionPropertiesByAssetId = createCachedSelector(
  selectAssetTranscriptionProperties,
  getVersionId,
  (allTranscriptionProperties, versionId) => {
    const versionTranscriptionProperties = allTranscriptionProperties.filter(
      ({ version_id }) => (versionId ? version_id === versionId : true),
    );
    // For legacy reason we might have multiple transcription properties for the same language
    // in which case we only want to return the latest one
    const groupedByLanguage = groupBy(
      versionTranscriptionProperties,
      'language',
    );
    return Object.keys(groupedByLanguage)
      .map((language) => groupedByLanguage[language].at(-1))
      .filter((properties) => properties !== undefined);
  },
)(
  (_: AppState, assetId: string, versionId: string | undefined) =>
    `${assetId}_${versionId ?? ''}`,
);

export const selectSelectedTranscriptionPropertiesIdByAssetId =
  createCachedSelector(
    getTranscriptionProperties,
    getSelectedTranscriptionPropertiesIdByAssetId,
    (transcriptionProperties, selectedTranscriptionPropertiesId) =>
      selectedTranscriptionPropertiesId !== undefined
        ? transcriptionProperties[selectedTranscriptionPropertiesId]
        : undefined,
  )((_state: AppState, assetId: string) => assetId);

export const selectTranscriptionSegmentsByAssetId = createCachedSelector(
  selectAssetSegments,
  getSelectedTranscriptionPropertiesIdByAssetId,
  (
    segments,
    selectedTranscriptionPropertiesId,
  ): SegmentEntityWithIdNormalized[] =>
    segments.filter(
      (segment) =>
        segment.transcription_id === selectedTranscriptionPropertiesId &&
        segment.status === 'ACTIVE',
    ),
)((_: AppState, assetId: string) => assetId);
